// SolveQuadraticWithoutCTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { toTextFractionLatex } from "../../../../utils/fractionLatex";

// === UTIL ===
function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  for (let d = 1; d <= 10; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;
      return intPart === 0
        ? `${sign}\\tfrac{${num}}{${den}}`
        : `${sign}${intPart}\\tfrac{${num}}{${den}}`;
    }
  }

  return `${sign}${abs.toFixed(2)}`;
}

// === GENERATE TASK ===
export function generateTask() {
  let a, b, x1, x2;

  do {
    a = Math.floor(Math.random() * 5 + 1) * (Math.random() < 0.5 ? -1 : 1);
    b = Math.floor(Math.random() * 10 + 1) * (Math.random() < 0.5 ? -1 : 1);
    x1 = 0;
    x2 = -b / a;
  } while (!isFinite(x2) || Math.abs(x2) > 20);

  const ax = a === 1 ? "x^2" : a === -1 ? "-x^2" : `${a}x^2`;
  const bx = (() => {
    const abs = Math.abs(b);
    const coef = abs === 1 ? "" : abs;
    return b > 0 ? `+ ${coef}x` : `- ${coef}x`;
  })();

  const latex = `${ax} ${bx} = 0`;

  return {
    type: "SolveQuadraticWithoutCTask",
    data: { a, b, x1, x2, latex }
  };
}

// === RENDER TASK ===
export function renderTask({ data }) {
  // FIX: przekazujemy czysty LaTeX, bez dodatkowych '${...}'
  return <p>Rozwiąż równanie: <InlineMath math={data.latex} /></p>;
}

// === INPUT ===
export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_1 ='} /></span>
        <FractionInput
          value={userInput?.x1 || ""}
          onChange={val => onChange({ ...userInput, x1: val })}
          disabled={disabled}
          label="x₁"
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_2 ='} /></span>
        <FractionInput
          value={userInput?.x2 || ""}
          onChange={val => onChange({ ...userInput, x2: val })}
          disabled={disabled}
          label="x₂"
        />
      </div>
    </div>
  );
}

// === CHECK ANSWER ===
export function checkAnswer({ data }, userInput) {
  const { x1, x2 } = data;
  const u1 = userInput?.x1?.parsed;
  const u2 = userInput?.x2?.parsed;

  const correct =
    (Math.abs(u1 - x1) < 0.01 && Math.abs(u2 - x2) < 0.01) ||
    (Math.abs(u1 - x2) < 0.01 && Math.abs(u2 - x1) < 0.01);

  return { correct, expected: [x1, x2] };
}

// === RENDER ANSWER ===
// Krótka odpowiedź – do lewej, InlineMath.
export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <div><InlineMath math={`x_1 = ${toTextFractionLatex(formatFractionLatex(data.x1))}`} /></div>
      <div><InlineMath math={`x_2 = ${toTextFractionLatex(formatFractionLatex(data.x2))}`} /></div>
    </div>
  );
}

// === RENDER EXPLANATION ===
// Wyniki w nowych linijkach z przepisaniem lewej strony; końcowa odpowiedź wyśrodkowana.
// Usunięto uwagi w nawiasach z tytułu kroku 3.
export function renderExplanation({ data }) {
  const { a, b, x2 } = data;

  const aStr = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  const bStr = b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`;
  const equation = `${aStr} ${bStr} = 0`;
  const numerator = -b;
  const denominator = a;

  return (
    <div className="space-y-3">
      <p><strong>Krok 1:</strong> Wyłączamy <InlineMath math={'x'} /> przed nawias:</p>
      <BlockMath math={`x(${aStr} ${bStr}) = 0`} />

      <p><strong>Krok 2:</strong> Równanie spełnione, gdy:</p>
      <BlockMath math={`x = 0 \\quad \\text{lub} \\quad ${equation}`} />

      <p><strong>Krok 3:</strong> Rozwiązujemy drugie równanie:</p>
      <BlockMath math={`\\begin{aligned}
${aStr} ${bStr} &= 0 \\\\
${aStr} &= ${-b} \\\\
x &= \\dfrac{${numerator}}{${denominator}} \\\\
x &= ${formatFractionLatex(x2)}
\\end{aligned}`} />

      <p><strong>Odpowiedź końcowa:</strong></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x_1 = 0,\\quad x_2 = ${formatFractionLatex(x2)}`} />
      </div>
    </div>
  );
}

// === RENDER USER ANSWER ===
export function renderUserAnswer(userInput) {
  if (
    !userInput ||
    typeof userInput !== "object" ||
    !userInput.x1?.raw ||
    !userInput.x2?.raw
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const x1 = formatLatexPreview(userInput.x1.raw, "x_1");
  const x2 = formatLatexPreview(userInput.x2.raw, "x_2");

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <div><InlineMath math={x1} /></div>
      <div style={{ marginTop: "0.5rem" }}>
        <InlineMath math={x2} />
      </div>
    </div>
  );
}
